// Feedback Button Widget
// Fixed position feedback button that appears on all pages

import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import FeedbackModal from './FeedbackModal';

const FeedbackButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasOtherModalOpen, setHasOtherModalOpen] = useState(false);

  console.log('FeedbackButton rendered, isModalOpen:', isModalOpen); // Debug log

  // Check for other modals being open by looking for modal backdrops
  useEffect(() => {
    const checkForModals = () => {
      // Look for modal backdrop patterns: fixed inset-0 with bg-black/opacity or similar
      // This is the most common pattern for modals
      const backdropSelectors = [
        'div.fixed.inset-0[class*="bg-black"]',
        'div.fixed.inset-0[class*="bg-opacity"]',
        'div[class*="backdrop"]',
        '[role="dialog"]',
        '[aria-modal="true"]'
      ];
      
      let foundModal = false;
      
      // Check each selector
      backdropSelectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            const styles = window.getComputedStyle(element);
            const display = styles.display;
            const visibility = styles.visibility;
            const opacity = parseFloat(styles.opacity) || 1;
            const zIndex = parseInt(styles.zIndex) || 0;
            
            // Check if it's visible and has a high z-index (modal-like)
            if (
              display !== 'none' &&
              visibility !== 'hidden' &&
              opacity > 0 &&
              zIndex >= 40 && // Modals typically have z-50 or higher, but we check for 40+ to be safe
              element !== document.querySelector('[class*="FeedbackButton"]')?.closest('div')
            ) {
              foundModal = true;
            }
          });
        } catch (e) {
          // Ignore selector errors
        }
      });
      
      // Also check for elements with high z-index that are fixed and cover the screen
      const highZElements = document.querySelectorAll('[class*="z-50"], [class*="z-[50"], [class*="z-[60"], [class*="z-[70"], [class*="z-[80"], [class*="z-[90"], [class*="z-[100"], [class*="z-[9999]"]');
      highZElements.forEach(element => {
        const styles = window.getComputedStyle(element);
        if (styles.position === 'fixed' && styles.zIndex && parseInt(styles.zIndex) >= 50) {
          // Check if it's a backdrop (covers full screen)
          const rect = element.getBoundingClientRect();
          if (rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9) {
            foundModal = true;
          }
        }
      });
      
      setHasOtherModalOpen(foundModal && !isModalOpen);
    };

    // Check initially
    checkForModals();
    
    // Use MutationObserver to watch for modal additions/removals
    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'role', 'aria-modal']
    });

    // Also check periodically as a fallback
    const interval = setInterval(checkForModals, 300);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [isModalOpen]);

  const handleOpenModal = () => {
    console.log('Feedback button clicked!'); // Debug log
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Hide button if any modal is open (including this one)
  if (isModalOpen || hasOtherModalOpen) {
    return (
      <>
        <FeedbackModal 
          isOpen={isModalOpen} 
          onClose={handleCloseModal}
        />
      </>
    );
  }

  return (
    <>
      {/* Feedback Button - Fixed position bottom right */}
      {/* On mobile: 16px from bottom nav bar (nav ~64px tall) and 16px from right edge for equal spacing */}
      <div className="fixed bottom-16 md:bottom-6 right-4 md:right-6 z-[60]">
        <div className="relative">
          {/* Tooltip */}
          {isHovered && (
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap">
              Share your feedback
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          )}
          
          {/* Main Button */}
          <button
            onClick={handleOpenModal}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="relative w-14 h-14 rounded-full bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center cursor-pointer"
            aria-label="Share your feedback"
          >
            <MessageCircle className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
      />
    </>
  );
};

export default FeedbackButton;
